import { useCallback, useEffect, useState } from 'react';

import { alterarCampo, listarCampos, obterPerfis, salvarPerfis } from '../../conteudo/api/conteudo';
import type { CampoConteudo, CamposDaFase, PerfisDoPlayer1 } from '../../conteudo/api/conteudo';
import { CampoDoConteudo, chaveDoCampo, mensagemDaFalha, SETA, valorTipado } from './comum';
import type { Aviso } from './comum';
import { PerfisDoPlayer1Cartao, perfisEmEdicao, perfisParaEnviar, problemaDosPerfis } from './PerfisDoPlayer1';
import type { PerfilEmEdicao } from './PerfisDoPlayer1';

const LETRAS = ['A', 'B', 'C', 'D'] as const;

const numeroDo = (local: string, prefixo: string) => Number(local.replace(prefixo, ''));

/**
 * A aba Player 1 de Configurações, no padrão das outras fases: a Jornada (abertura e encerramento),
 * o seletor de capítulo (o servidor não deixa criar capítulos) e o capítulo marcado com a situação
 * e as quatro escolhas, cada uma com a justificativa e os efeitos no perfil, e os Perfis (`RA13c`).
 * O Player 1 não tem regra de pontuação na tela. Tudo fica pendente até "Salvar Alterações".
 */
export function Player1Configuracao({ csrfToken }: { csrfToken: string }) {
    const [dados, setDados] = useState<CamposDaFase | null>(null);
    const [perfis, setPerfis] = useState<PerfisDoPlayer1 | null>(null);
    const [perfisRascunho, setPerfisRascunho] = useState<PerfilEmEdicao[] | null>(null);
    const [falhouCarregar, setFalhouCarregar] = useState(false);
    const [rascunho, setRascunho] = useState<Record<string, string>>({});
    const [selecionado, setSelecionado] = useState(1);
    const [jornadaAberta, setJornadaAberta] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const [aviso, setAviso] = useState<Aviso | null>(null);

    const carregar = useCallback(async () => {
        try {
            const [campos, tabela] = await Promise.all([listarCampos('player1'), obterPerfis()]);
            setDados(campos);
            setPerfis(tabela);
            setFalhouCarregar(false);
        } catch {
            setFalhouCarregar(true);
        }
    }, []);

    useEffect(() => {
        void carregar();
    }, [carregar]);

    if (falhouCarregar) {
        return (
            <p className="estado-variaveis mensagem-login">
                Não foi possível carregar o Player 1.{' '}
                <button className="botao-texto" type="button" onClick={() => void carregar()}>Tentar novamente</button>
            </p>
        );
    }
    if (!dados || !perfis) return <p className="estado-variaveis">Carregando o Player 1…</p>;

    const travado = !dados.editavel;
    const perfisNaTela = perfisRascunho ?? perfisEmEdicao(perfis.perfis);
    const problemaNosPerfis = problemaDosPerfis(perfis, perfisNaTela);
    const doLocal = (local: string) => dados.campos.filter((campo) => campo.local === local);
    const jornada = dados.campos.find((campo) => campo.local.startsWith('Jornada'))?.local;
    const capitulos = [...new Set(dados.campos.filter((campo) => /^Capítulo \d+$/.test(campo.local)).map((campo) => campo.local))]
        .sort((a, b) => numeroDo(a, 'Capítulo ') - numeroDo(b, 'Capítulo '));
    const marcado = selecionado >= 1 && selecionado <= capitulos.length ? selecionado : 1;
    const capituloMarcado = capitulos[marcado - 1];

    const valorDoCampo = (campo: CampoConteudo) => rascunho[chaveDoCampo(campo)] ?? (campo.valor === null ? '' : String(campo.valor));
    const desenharCampo = (campo: CampoConteudo) => (
        <CampoDoConteudo
            key={chaveDoCampo(campo)}
            campo={campo}
            valor={valorDoCampo(campo)}
            travado={travado}
            aoMudar={(valor) => setRascunho((atual) => ({ ...atual, [chaveDoCampo(campo)]: valor }))}
        />
    );

    async function salvar() {
        if (!dados || !perfis) return;
        setAviso(null);
        setSalvando(true);
        try {
            for (const campo of dados.campos) {
                const digitado = rascunho[chaveDoCampo(campo)];
                const original = campo.valor === null ? '' : String(campo.valor);
                if (digitado === undefined || digitado === original) continue;
                await alterarCampo(campo, valorTipado(campo, digitado), campo.valor, csrfToken);
            }
            setRascunho({});
            // Os perfis vão de uma vez só: no meio de uma edição a tabela fica desorganizada.
            if (perfisRascunho) {
                await salvarPerfis(perfis.versao, perfisParaEnviar(perfisRascunho), csrfToken);
                setPerfisRascunho(null);
            }
            setAviso({ tipo: 'sucesso', texto: 'Alterações salvas.' });
        } catch (erro) {
            setAviso({ tipo: 'erro', texto: mensagemDaFalha(erro) });
        } finally {
            setSalvando(false);
            await carregar();
        }
    }

    return (
        <div className="config-fase">
            {dados.turmas_com_fase_aberta.length > 0 && (
                <p className="aviso-fase-aberta" role="status">
                    A fase Player 1 está aberta na turma {dados.turmas_com_fase_aberta.join(', ')}: só leitura até ela fechar.
                </p>
            )}

            {jornada && (
                <section className="cartao-config" aria-labelledby="player1-jornada">
                    <div className="cabecalho-cartao-config">
                        <h3 id="player1-jornada">Jornada</h3>
                        <button
                            type="button"
                            className={`seta-config${jornadaAberta ? ' seta-config-aberta' : ''}`}
                            aria-label={jornadaAberta ? 'Recolher Jornada' : 'Abrir Jornada'}
                            aria-expanded={jornadaAberta}
                            onClick={() => setJornadaAberta((aberta) => !aberta)}
                        >
                            {SETA}
                        </button>
                    </div>
                    {jornadaAberta && doLocal(jornada).map(desenharCampo)}
                </section>
            )}

            <div className="seletor-enigma-config">
                <label>
                    <span>Capítulo:</span>
                    <select aria-label="Capítulo" value={String(marcado)} onChange={(evento) => setSelecionado(Number(evento.target.value))}>
                        {capitulos.map((_, i) => (
                            <option key={i + 1} value={String(i + 1)}>{i + 1}</option>
                        ))}
                    </select>
                </label>
            </div>

            {capituloMarcado && (
                <section className="cartao-config" aria-labelledby="player1-capitulo">
                    <div className="cabecalho-cartao-config">
                        <h3 id="player1-capitulo">{capituloMarcado}</h3>
                    </div>
                    {doLocal(capituloMarcado).map(desenharCampo)}
                    {LETRAS.map((letra) => {
                        const daOpcao = doLocal(`${capituloMarcado} › Opção ${letra}`);
                        if (daOpcao.length === 0) return null;
                        const efeitos = daOpcao.filter((campo) => campo.campo.startsWith('efeitos.'));
                        return (
                            <section key={letra} className="cartao-pista" aria-labelledby={`player1-opcao-${letra}`}>
                                <div className="cabecalho-cartao-config">
                                    <h4 id={`player1-opcao-${letra}`}>Opção {letra}</h4>
                                </div>
                                {daOpcao.filter((campo) => !campo.campo.startsWith('efeitos.')).map(desenharCampo)}
                                {efeitos.length > 0 && (
                                    <div className="efeitos-config" role="group" aria-label={`Efeitos da Opção ${letra}`}>
                                        <p className="rotulo-alternativas">Efeitos no perfil <span>(podem ser negativos)</span></p>
                                        <div className="grade-efeitos">{efeitos.map(desenharCampo)}</div>
                                    </div>
                                )}
                            </section>
                        );
                    })}
                </section>
            )}

            <PerfisDoPlayer1Cartao dados={perfis} perfis={perfisNaTela} aoMudar={setPerfisRascunho} travado={travado} />

            {aviso && (
                <p className={aviso.tipo === 'erro' ? 'mensagem-login aviso-cadastro' : 'mensagem-salvo'} role={aviso.tipo === 'erro' ? 'alert' : 'status'}>
                    {aviso.texto}
                </p>
            )}

            <div className="rodape-config">
                <button type="button" className="botao-cancelar-config" onClick={() => {
                    setRascunho({});
                    setPerfisRascunho(null);
                }} disabled={salvando}>
                    Cancelar
                </button>
                <button type="button" className="botao-salvar-config" onClick={() => void salvar()} disabled={salvando || travado || problemaNosPerfis !== null}>
                    {salvando ? 'Salvando…' : 'Salvar Alterações'}
                </button>
            </div>
        </div>
    );
}
